import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map as _map, flatMap, includes } from 'lodash';
import { Observable, forkJoin, map, tap } from 'rxjs';

import { Document, DocumentCreate } from '@lockton-apps/shared/interfaces';
import { toFormData } from '@lockton-apps/shared/util/helpers';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  modelPlural = 'documents';
  modelSingular = 'document';

  constructor(
    private http: HttpClient,
    @Inject('BASE_API_URL') private baseUrl: string,
    @Inject('AZURE_STORAGE_ACCOUNT_NAME') private storageAccountName: string
  ) {}

  allForFolder(folderId: number | null) {
    return this.http
      .get<Document[]>(this._getUrlForFolder(folderId))
      .pipe(map((documents) => this.mapWithReadonly(documents)));
  }

  allForFolders(folderIds: number[]) {
    const requests = _map(folderIds, (folderId) => this.allForFolder(folderId));
    return forkJoin(requests).pipe(
      map((documents) =>
        flatMap(documents, (document) => this.mapWithReadonly(document))
      )
    );
  }

  allForPolicy(policyId: number) {
    return this.http
      .get<Document[]>(this._getUrlForPolicy(policyId))
      .pipe(map((documents) => this.mapWithReadonly(documents)));
  }

  allTags() {
    return this.http.get<any[]>(this._getUrlForTags());
  }

  add(document: DocumentCreate) {
    const formData = toFormData({
      ...document,
      storageAccountName: this.storageAccountName,
    });
    return this.http.post<Document>(this._getUrlSingular(), formData);
  }

  update(document: Partial<Document>) {
    return this.http.patch<Document>(this._getUrlSingular(), document);
  }

  delete(id: number) {
    return this.http.delete<Document>(`${this._getUrlSingular()}/${id}`);
  }

  download(id: number | string, name: string = 'filename'): Observable<Blob> {
    return this.http
      .get(`${this._getUrlSingular()}/file/${id}`, {
        responseType: 'blob',
      })
      .pipe(
        tap((file) => {
          const tempLink: HTMLAnchorElement =
            window.document.createElement('a');
          tempLink.setAttribute('target', '_blank');

          // Turn off pdf preview until further notice
          // if (!endsWith(name, '.pdf')) {
          //   tempLink.download = name;
          // }
          tempLink.download = name;
          tempLink.href = URL.createObjectURL(file);

          tempLink.click();
          tempLink.remove();
        })
      );
  }

  batchDownload(documentIds: number[], clientId: number) {
    return this.http
      .post(
        `${this.baseUrl}/documents/bundle`,
        { documentIds, clientId },
        {
          responseType: 'blob',
          observe: 'response',
          headers: {
            'X-User-TimeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }
      )
      .pipe(
        tap((res) => {
          const contentDisposition = res.headers.get('content-disposition');
          if (!res.body) {
            throw new Error('No file found');
          }
          if (!contentDisposition) {
            throw new Error('No file name found');
          }
          const fileName = this.parseFileName(contentDisposition);
          if (!fileName) {
            throw new Error('Unable to parse filename');
          }

          const tempLink: HTMLAnchorElement =
            window.document.createElement('a');
          tempLink.setAttribute('target', '_blank');
          tempLink.download = fileName;
          tempLink.href = URL.createObjectURL(res.body);

          tempLink.click();
          tempLink.remove();
        })
      );
  }

  private _getUrlPlural() {
    return `${this.baseUrl}/${this.modelPlural}`;
  }

  private _getUrlSingular() {
    return `${this.baseUrl}/${this.modelSingular}`;
  }

  private _getUrlForFolder(folderId: number | null) {
    return `${this._getUrlPlural()}/folder/${folderId || ''}`;
  }

  private _getUrlForPolicy(policyId: number) {
    return `${this._getUrlPlural()}/policy/${policyId}`;
  }

  private _getUrlForTags() {
    return `${this._getUrlPlural()}/tags`;
  }

  private parseFileName(contentDisposition: string) {
    const regex = /filename="?(?:UTF-8'')?(.+?)"?(?:;|$)/;
    const match = contentDisposition.match(regex);
    if (match) {
      return decodeURIComponent(match[1]);
    } else {
      return null;
    }
  }

  private mapWithReadonly(documents: Document[]) {
    return _map(documents, (document) => ({
      ...document,
      readonly: includes(document.tags, 'Proposal Form'),
    }));
  }
}
